import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { UserRead } from "client/jspPlatformExperiment";
import jspPlatformApi from "client/portals";
import {
  isApiError,
  isErrorResponseBodyType,
  toJspApiError,
} from "models/utils/apiUtils";
import { applyAccountLanguage } from "utils/language";

interface UserAccountStateProps {
  /** Return value of user account data from the Jasper Platform API call */
  userAccount: UserRead | null;
  isLoading: boolean;
  error: string | null;
}

const initialState: UserAccountStateProps = {
  userAccount: null,
  isLoading: false,
  error: null,
};

const fetchUserAccount = createAsyncThunk(
  "userAccount/fetchUserAccount",
  async (input: { userId: number }, { rejectWithValue }) => {
    const { userId } = input;
    try {
      const res = await jspPlatformApi().user.getUserAccountByUserId({
        path: { user_id: userId },
      });
      applyAccountLanguage(res.data ?? undefined);
      return res.data ?? null;
    } catch (error) {
      let errMsg: string;
      const err = isApiError(error) ? toJspApiError(error) : null;
      if (err && isErrorResponseBodyType(err.body)) {
        errMsg = err.body.detail;
      } else {
        errMsg = "Unknown error!";
      }
      return rejectWithValue(errMsg);
    }
  },
);

const updateUserAccount = (
  state: UserAccountStateProps,
  action: PayloadAction<UserRead | null>,
): UserAccountStateProps => {
  return {
    ...state,
    userAccount: action.payload,
  };
};

export const userAccountSlice = createSlice({
  name: "userAccountSlice",
  initialState,
  reducers: {
    setUserAccount: updateUserAccount,
    reset: () => {
      return initialState;
    },
  },
  extraReducers: (builder) => {
    builder.addCase(fetchUserAccount.pending, (state) => {
      return { ...state, isLoading: true };
    });
    builder.addCase(fetchUserAccount.fulfilled, (state, action) => {
      return {
        ...state,
        ...updateUserAccount(state, action),
        isLoading: false,
      };
    });
    builder.addCase(fetchUserAccount.rejected, (state, action) => {
      return { ...state, isLoading: false, error: action.payload as string };
    });
  },
});

export const userAccountActions = {
  ...userAccountSlice.actions,
  fetchUserAccount,
};

const userAccountSliceReducer = userAccountSlice.reducer;

export default userAccountSliceReducer;
